export const dummyBlogHtml = {
  intro: `
    <h1>Complete 5 Marla House Design Guide for Pakistani Families</h1>
    <p>A 5 marla plot is one of the most common family sizes in Lahore, Islamabad, Faisalabad and Karachi suburbs. With a typical front of <strong>25 feet</strong> and depth of <em>45 feet</em>, the house can still feel open if the lounge, stairs and kitchen are stacked with care. This guide covers layout, ventilation, car porch and a realistic finishing budget.</p>
    <p>Most clients ask for a drawing room, two bedrooms with attached baths, an open kitchen, a store and a compact car porch. The mistake is placing a wide staircase in the centre. Keep circulation on one side so the lounge gets a full-width window to the front lawn.</p>
    <p style="text-align: center">Good design is not more rooms. It is better light, air and storage in the rooms you already need.</p>
    <blockquote><p>A well planned 5 marla house can feel spacious if the layout, ventilation and furniture sizes are chosen before grey structure starts.</p></blockquote>
    <p>Use <span style="color: #982598">zoned furniture</span>, <mark data-color="#fde68a" style="background-color: #fde68a">cross ventilation</mark> and a <u>single-flight stair</u> wherever the plot allows. Avoid a <s>dark central corridor</s> that wastes 80–100 sq ft.</p>
    <hr>
  `,
  lists: `
    <h2>Room planning checklist</h2>
    <p>Walk through this list with your architect before the first 2D drawing. Changing a bathroom stack later costs both time and plumbing.</p>
    <h3>Must-have rooms</h3>
    <ul>
      <li><p>Drawing room with guest seating for 6–8 people</p></li>
      <li><p>Two bedrooms with attached baths</p>
        <ul>
          <li><p>Master bedroom on the quieter rear side</p></li>
          <li><p>Kids or parents room near the family lounge</p></li>
        </ul>
      </li>
      <li><p>Open kitchen with a 5x6 store and service yard</p></li>
      <li><p>Powder bath for guests on the ground floor</p></li>
    </ul>
    <h3>Construction sequence</h3>
    <ol>
      <li><p>Soil test, plot survey and foundation layout</p></li>
      <li><p>Grey structure</p>
        <ol>
          <li><p>Columns, beams and water-tank upstands</p></li>
          <li><p>Slab, brickwork and staircase waist slab</p></li>
        </ol>
      </li>
      <li><p>MEP rough-in, plaster, tiles, paint and fittings</p></li>
    </ol>
    <h3>Client tasks before design freeze</h3>
    <ul data-type="taskList">
      <li data-type="taskItem" data-checked="true"><label><input type="checkbox" checked><span></span></label><div><p>Share exact plot dimensions and road side</p></div></li>
      <li data-type="taskItem" data-checked="true"><label><input type="checkbox" checked><span></span></label><div><p>Confirm car porch vs extra lounge space</p></div></li>
      <li data-type="taskItem" data-checked="false"><label><input type="checkbox"><span></span></label><div><p>Approve front elevation style and material palette</p></div></li>
      <li data-type="taskItem" data-checked="false"><label><input type="checkbox"><span></span></label><div><p>Sign off 3D interior mood board for kitchen and baths</p></div></li>
    </ul>
  `,
  table: `
    <h2>Covered area and estimated cost</h2>
    <p>Figures below are for a double-storey 5 marla house with a small terrace. Rates are 2026 Lahore averages for grey structure plus standard finishing, not luxury imported fittings. Always add 8–10% contingency.</p>
    <table>
      <tbody>
        <tr>
          <th><p>Floor</p></th>
          <th><p>Covered area</p></th>
          <th><p>Main rooms</p></th>
          <th><p>Est. cost</p></th>
        </tr>
        <tr>
          <td><p>Ground</p></td>
          <td><p>900 sq ft</p></td>
          <td><p>Lounge, kitchen, 1 bed, powder bath, porch</p></td>
          <td><p>PKR 32 lac</p></td>
        </tr>
        <tr>
          <td><p>First</p></td>
          <td><p>850 sq ft</p></td>
          <td><p>2 beds, family lounge, 2 baths</p></td>
          <td><p>PKR 28 lac</p></td>
        </tr>
        <tr>
          <td><p>Mumty / terrace</p></td>
          <td><p>220 sq ft</p></td>
          <td><p>Stair head, tank, open terrace</p></td>
          <td><p>PKR 7 lac</p></td>
        </tr>
        <tr>
          <td><p>Total</p></td>
          <td><p>1,970 sq ft</p></td>
          <td><p>Double storey family house</p></td>
          <td><p>PKR 67 lac</p></td>
        </tr>
      </tbody>
    </table>
    <p>Need typical 25x45 drawings? See the <a target="_blank" rel="noopener noreferrer nofollow" href="https://gharplans.com">Gharplans 5 marla collection</a> and match your facing (north, south or east) before you copy a layout.</p>
    <blockquote><p>Do not copy a west-facing plan onto an east-facing plot. Kitchen heat and afternoon glare will fight the same windows.</p></blockquote>
  `,
  extras: `
    <h2>Finishing, ventilation and FAQs</h2>
    <p>Keep lounge furniture under <span style="font-size: 20px">7 feet width</span> so walkways stay clear. Use <span style="font-size: 12px">12px captions on drawings</span> so dimensions stay readable on A3 prints.</p>
    <p>For summer comfort, place windows on opposite walls. A <span style="line-height: 2">high ventilator above the kitchen cabinets takes steam out without stealing wall space from the fridge and stove.</span></p>
    <p>Material hint: <mark data-color="#bbf7d0" style="background-color: #bbf7d0">matt tiles hide dust better than glossy white</mark> in the car porch and stairs.</p>
    <pre><code>plotWidth = 25;
plotDepth = 45;
maxCoverage = 0.80;
coveredArea = plotWidth * plotDepth * maxCoverage;</code></pre>
    <details open>
      <summary>Frequently asked questions</summary>
      <div data-type="detailsContent">
        <p>Can a car porch fit in 5 marla? Yes, if the lounge is stacked on the first floor or the drawing room is kept 11x13 instead of 14x16.</p>
        <p>Should the kitchen be open? Open kitchens work when a store and wash yard sit behind, and the hob is not facing the sofa.</p>
        <p>Is a basement useful? Only if the water table is low and you need a servant room or extra lounge. It is rarely worth the cost on a 5 marla plot.</p>
      </div>
    </details>
    <p>Useful marks on drawings: — party wall, – finished floor, … future terrace stair, • light point, © north, ® revision, ™ typical window, ✓ client approved.</p>
  `,
  urduIntro: `
    <h1>پاکستانی خاندانوں کے لیے پانچ مرلہ گھر ڈیزائن گائیڈ</h1>
    <p>پانچ مرلہ پلاٹ لاہور، اسلام آباد، فیصل آباد اور کراچی کے نواحی علاقوں میں سب سے عام خاندانی سائز ہے۔ عام طور پر سامنے <strong>۲۵ فٹ</strong> اور گہرائی <em>۴۵ فٹ</em> ہوتی ہے۔ اگر ڈرائنگ روم، سیڑھیاں اور کچن درست جگہ پر ہوں تو گھر تنگ نہیں لگتا۔</p>
    <p>زیادہ تر کلائنٹس ڈرائنگ روم، دو بیڈ روم بمع باتھ، اوپن کچن، سٹور اور چھوٹا کار پورچ مانگتے ہیں۔ سب سے بڑی غلطی چوڑی سیڑھی بیچ میں رکھنا ہے۔ گردش ایک طرف رکھیں تاکہ lounge کو سامنے لان کی طرف پوری کھڑکی ملے۔</p>
    <p style="text-align: center">اچھا ڈیزائن زیادہ کمروں کا نام نہیں، روشنی، ہوا اور سٹوریج کا نام ہے۔</p>
    <blockquote><p>اگر لے آؤٹ، وینٹیلیشن اور فرنیچر کا سائز گرے سٹرکچر سے پہلے طے ہو جائے تو پانچ مرلہ گھر بھی کشادہ محسوس ہوتا ہے۔</p></blockquote>
    <p><span style="color: #982598">زون والا فرنیچر</span>، <mark data-color="#fde68a" style="background-color: #fde68a">کراس وینٹیلیشن</mark> اور جہاں ممکن ہو <u>سنگل فلائٹ سیڑھی</u> استعمال کریں۔ <s>اندھیرا درمیانی کوریڈور</s> ۸۰ سے ۱۰۰ مربع فٹ ضائع کر دیتا ہے۔</p>
    <hr>
  `,
  urduLists: `
    <h2>کمروں کی منصوبہ بندی</h2>
    <p>پہلی ۲ ڈی ڈرائنگ سے پہلے یہ فہرست آرکیٹیکٹ کے ساتھ چیک کریں۔ بعد میں باتھ کی پوزیشن بدلنا پلمبنگ اور بجٹ دونوں خراب کرتا ہے۔</p>
    <h3>ضروری کمرے</h3>
    <ul>
      <li><p>ڈرائنگ روم جس میں ۶ سے ۸ مہمان بیٹھ سکیں</p></li>
      <li><p>دو بیڈ روم بمع اٹیچڈ باتھ</p>
        <ul>
          <li><p>ماسٹر بیڈ روم پچھلی پرسکون سائیڈ پر</p></li>
          <li><p>بچوں یا والدین کا کمرہ فیملی لاؤنج کے قریب</p></li>
        </ul>
      </li>
      <li><p>اوپن کچن ساتھ ۵ در ۶ سٹور اور سروس یارڈ</p></li>
      <li><p>گراؤنڈ فلور پر مہمانوں کے لیے پاؤڈر باتھ</p></li>
    </ul>
    <h3>تعمیر کا ترتیب وار کام</h3>
    <ol>
      <li><p>مٹی کا ٹیسٹ، پلاٹ سروے اور فاؤنڈیشن</p></li>
      <li><p>گرے سٹرکچر</p>
        <ol>
          <li><p>کالم، بیم اور پانی کی ٹنکی</p></li>
          <li><p>سلیب، اینٹوں کا کام اور سیڑھی</p></li>
        </ol>
      </li>
      <li><p>بجلی پانی، پلستر، ٹائلیں، پینٹ اور فٹنگز</p></li>
    </ol>
    <h3>ڈیزائن فائنل سے پہلے کلائنٹ کے کام</h3>
    <ul data-type="taskList">
      <li data-type="taskItem" data-checked="true"><label><input type="checkbox" checked><span></span></label><div><p>پلاٹ کی درست پیمائش اور سڑک کی سمت بھیجیں</p></div></li>
      <li data-type="taskItem" data-checked="true"><label><input type="checkbox" checked><span></span></label><div><p>کار پورچ یا اضافی لاؤنج میں سے ایک منتخب کریں</p></div></li>
      <li data-type="taskItem" data-checked="false"><label><input type="checkbox"><span></span></label><div><p>فرنٹ ایلیویشن اور میٹیریل منظور کریں</p></div></li>
      <li data-type="taskItem" data-checked="false"><label><input type="checkbox"><span></span></label><div><p>کچن اور باتھ کا ۳ ڈی موڈ بورڈ منظور کریں</p></div></li>
    </ul>
  `,
  urduTable: `
    <h2>ڈھکا ہوا رقبہ اور تخمینہ لاگت</h2>
    <p>نیچے اعدادوشمار ڈبل سٹوری پانچ مرلہ گھر کے ہیں۔ یہ ۲۰۲۶ کے لاہور کے اوسط ریٹس ہیں، لگژری امپورٹڈ فٹنگز کے بغیر۔ ہمیشہ ۸ سے ۱۰ فیصد اضافی بجٹ رکھیں۔</p>
    <table>
      <tbody>
        <tr>
          <th><p>منزل</p></th>
          <th><p>رقبہ</p></th>
          <th><p>اہم کمرے</p></th>
          <th><p>تخمینہ لاگت</p></th>
        </tr>
        <tr>
          <td><p>گراؤنڈ</p></td>
          <td><p>۹۰۰ مربع فٹ</p></td>
          <td><p>لاؤنج، کچن، ایک بیڈ، پاؤڈر باتھ، پورچ</p></td>
          <td><p>۳۲ لاکھ</p></td>
        </tr>
        <tr>
          <td><p>فرسٹ</p></td>
          <td><p>۸۵۰ مربع فٹ</p></td>
          <td><p>دو بیڈ، فیملی لاؤنج، دو باتھ</p></td>
          <td><p>۲۸ لاکھ</p></td>
        </tr>
        <tr>
          <td><p>ممتی / چھت</p></td>
          <td><p>۲۲۰ مربع فٹ</p></td>
          <td><p>سیڑھی، ٹنکی، اوپن ٹیرس</p></td>
          <td><p>۷ لاکھ</p></td>
        </tr>
        <tr>
          <td><p>کل</p></td>
          <td><p>۱٬۹۷۰ مربع فٹ</p></td>
          <td><p>ڈبل سٹوری خاندانی گھر</p></td>
          <td><p>۶۷ لاکھ</p></td>
        </tr>
      </tbody>
    </table>
    <p>۲۵ در ۴۵ کی عام ڈرائنگز <a target="_blank" rel="noopener noreferrer nofollow" href="https://gharplans.com">گھپلانس کے پانچ مرلہ کلیکشن</a> میں دیکھیں۔ لے آؤٹ کاپی کرنے سے پہلے پلاٹ کی سمت ضرور ملائیں۔</p>
    <blockquote><p>مغرب رخ والا پلان مشرق رخ پلاٹ پر مت لگاؤ۔ کچن کی گرمی اور دوپہر کی دھوپ ایک ہی کھڑکیوں سے ٹکرائے گی۔</p></blockquote>
  `,
  urduExtras: `
    <h2>فنشنگ، ہوا داری اور عام سوالات</h2>
    <p>لاؤنج کا فرنیچر <span style="font-size: 20px">۷ فٹ چوڑائی</span> سے کم رکھیں تاکہ گزرنے کی جگہ رہے۔ ڈرائنگ پر <span style="font-size: 12px">چھوٹی کیپشن</span> رکھیں تاکہ A3 پر پیمائش پڑھنے میں آسان رہے۔</p>
    <p>گرمی کے لیے کھڑکیاں مخالف دیواروں پر لگائیں۔ <span style="line-height: 2">کچن کی الماریوں کے اوپر وینٹیلیٹر بھاپ نکال دیتا ہے اور فرنٹ والا وال فریج کے لیے خالی رہتا ہے۔</span></p>
    <p>میٹیریل: <mark data-color="#bbf7d0" style="background-color: #bbf7d0">میٹ ٹائلیں چمکدار سفید سے بہتر گرد چھپاتی ہیں</mark> پورچ اور سیڑھیوں پر۔</p>
    <pre><code>plotWidth = 25;
plotDepth = 45;
maxCoverage = 0.80;
coveredArea = plotWidth * plotDepth * maxCoverage;</code></pre>
    <details open>
      <summary>اکثر پوچھے جانے والے سوالات</summary>
      <div data-type="detailsContent">
        <p>کیا پانچ مرلہ میں کار پورچ آ سکتا ہے؟ ہاں، اگر لاؤنج اوپر ہو یا ڈرائنگ روم ۱۴ در ۱۶ کی بجائے ۱۱ در ۱۳ ہو۔</p>
        <p>کیا کچن اوپن ہونا چاہیے؟ اوپن کچن تب ٹھیک ہے جب پیچھے سٹور اور واش یارڈ ہو، اور چولہا سوفا کے سامنے نہ ہو۔</p>
        <p>بیسمنٹ فائدہ مند ہے؟ صرف اگر پانی نیچے ہو اور نوکر روم یا اضافی لاؤنج درکار ہو۔ پانچ مرلہ پر اکثر لاگت کے قابل نہیں۔</p>
      </div>
    </details>
    <p>ڈرائنگ کے نشان: — پارٹی وال، – فنشڈ فلور، … مستقبل کی سیڑھی، • لائٹ پوائنٹ، © شمال، ® نظرثانی، ™ عام ونڈو، ✓ کلائنٹ منظور۔</p>
  `,
};

export const dummyBlogContentBlocks = [
  {
    text: dummyBlogHtml.intro.trim(),
    text_ur: dummyBlogHtml.urduIntro.trim(),
    textAlignment: "left",
    image: null,
    imageAlignment: "right",
  },
  {
    text: dummyBlogHtml.lists.trim(),
    text_ur: dummyBlogHtml.urduLists.trim(),
    textAlignment: "left",
    image: null,
    imageAlignment: "left",
  },
  {
    text: dummyBlogHtml.table.trim(),
    text_ur: dummyBlogHtml.urduTable.trim(),
    textAlignment: "left",
    image: null,
    imageAlignment: "right",
  },
  {
    text: dummyBlogHtml.extras.trim(),
    text_ur: dummyBlogHtml.urduExtras.trim(),
    textAlignment: "left",
    image: null,
    imageAlignment: "right",
  },
];

export const dummyBlogFormData = {
  name: "Complete 5 Marla House Design Guide for Pakistani Families",
  name_ur: "پاکستانی خاندانوں کے لیے پانچ مرلہ گھر ڈیزائن گائیڈ",
  description:
    "A practical 5 marla layout guide covering lounge stacking, car porch, ventilation, finishing materials and a 2026 Lahore cost table for a double-storey family house.",
  description_ur:
    "پانچ مرلہ گھر کی عملی گائیڈ: لاؤنج، کار پورچ، ہوا داری، فنشنگ میٹیریل اور ۲۰۲۶ لاہور کے ریٹس کے ساتھ ڈبل سٹوری خاندانی گھر کا تخمینہ۔",
  customUrl: "5-marla-house-design-guide",
  status: "draft",
  status_ur: "مسودہ",
};

export function applyDummyBlog(setFormData, setContentBlocks, firstCategoryId = "") {
  setFormData((prev) => ({
    ...prev,
    ...dummyBlogFormData,
    categoryId: prev.categoryId || firstCategoryId || "",
  }));
  setContentBlocks(dummyBlogContentBlocks.map((block) => ({ ...block })));
}
